import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Loader2, RefreshCw, CheckCircle2, XCircle } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface DocumentReprocessButtonProps {
  documentId: number
  documentName: string
  onReprocess: (documentId: number) => Promise<void>
  disabled?: boolean
  className?: string
}

export default function DocumentReprocessButton({
  documentId,
  documentName,
  onReprocess,
  disabled = false,
  className,
}: DocumentReprocessButtonProps) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleClick = async () => {
    if (isProcessing || disabled) return

    setIsProcessing(true)
    setStatus('idle')

    try {
      await onReprocess(documentId)
      setStatus('success')
      // Reset success status after 2 seconds
      setTimeout(() => setStatus('idle'), 2000)
    } catch (error) {
      setStatus('error')
      // Reset error status after 3 seconds
      setTimeout(() => setStatus('idle'), 3000)
    } finally {
      setIsProcessing(false)
    }
  }

  const getIcon = () => {
    if (isProcessing) {
      return <Loader2 className="w-4 h-4 animate-spin" />
    }
    if (status === 'success') {
      return <CheckCircle2 className="w-4 h-4 text-green-600" />
    }
    if (status === 'error') {
      return <XCircle className="w-4 h-4 text-red-600" />
    }
    return <RefreshCw className="w-4 h-4" />
  }

  const getTooltipText = () => {
    if (isProcessing) {
      return 'Reprocessing document...'
    }
    if (status === 'success') {
      return 'Document reprocessed successfully'
    }
    if (status === 'error') {
      return 'Failed to reprocess document'
    }
    return 'Reprocess document with current chunk settings'
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClick}
            disabled={disabled || isProcessing}
            className={cn(
              'text-blue-600 hover:text-blue-700 hover:bg-blue-50',
              status === 'success' && 'text-green-600 hover:text-green-700',
              status === 'error' && 'text-red-600 hover:text-red-700',
              className
            )}
            aria-label={`Reprocess ${documentName}`}
          >
            {getIcon()}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{getTooltipText()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
